import _typeof from "@babel/runtime/helpers/typeof";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { isSSR } from '@atlaskit/editor-common/core-utils';

/**
 * Represents the SSR data for a single provider.
 * It's a map where each key is a unique node data key and the value is the prefetched data for that node.
 *
 * @example
 * {
 *   'node-id-1': { value: 'some data' },
 *   'node-id-2': { value: 'other data' }
 * }
 */

/**
 * Represents the cached data for a Node Data Provider.
 * Each key is a unique node data key, and the value is an object containing:
 * - `source`: Indicates whether the data was fetched from SSR or the network.
 * - `data`: The actual data, which can be either a resolved value or a Promise.
 *
 * @example
 * {
 *   'node-id-1': { source: 'ssr', data: { value: 'some data' } },
 *   'node-id-2': { source: 'network', data: Promise.resolve({ value: 'other data' }) }
 * }
 */

/**
 * Represents the payload passed to the callback function when data is fetched.
 * It can either contain an error or the fetched data.
 */

/**
 * A Node Data Provider is responsible for fetching and caching data associated with specific ProseMirror nodes.
 * It supports a cache-first-then-network strategy, with initial data potentially provided via SSR.
 *
 * @template Node The specific type of JSONNode this provider supports.
 * @template Data The type of data this provider fetches and manages.
 */
export var NodeDataProvider = /*#__PURE__*/function () {
  function NodeDataProvider() {
    _classCallCheck(this, NodeDataProvider);
    this.cacheVersion = 0;
    this.cache = {};
  }

  /**
   * Sets the SSR data for the provider.
   * This pre-populates the cache with data rendered on the server, preventing redundant network requests on the client.
   * Calling this method will invalidate the existing cache.
   *
   * @example
   * ```
   * const ssrData = window.__SSR_NODE_DATA__ || {};
   * nodeDataProvider.setSSRData(ssrData);
   * ```
   *
   * @param ssrData A map of node data keys to their corresponding data.
   */
  return _createClass(NodeDataProvider, [{
    key: "setSSRData",
    value: function setSSRData() {
      var ssrData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      this.cacheVersion++;
      this.cache = Object.fromEntries(Object.entries(ssrData).map(function (_ref) {
        var _ref2 = _slicedToArray(_ref, 2),
          key = _ref2[0],
          data = _ref2[1];
        return [key, {
          data: data,
          source: 'ssr'
        }];
      }));
    }

    /**
     * Clears all cached data.
     * This increments the internal cache version, invalidating any pending network requests.
     *
     * @example
     * ```
     * function useMyNodeDataProvider(contentId: string) {
     *  const nodeDataProvider = new MyNodeDataProvider();
     *
     *  // Reset the cache when the contentId changes (e.g., when the user navigates to a different page).
     *  useEffect(() => {
     *    nodeDataProvider.resetCache();
     *  }, [contentId]);
     *
     *  return nodeDataProvider;
     * }
     * ```
     */
  }, {
    key: "resetCache",
    value: function resetCache() {
      this.cacheVersion++;
      this.cache = {};
    }

    /**
     * Fetches data for a given node using a cache-first-then-network strategy.
     *
     * The provided callback may be called multiple times:
     * 1. Immediately with data from the SSR cache, if available.
     * 2. Asynchronously with data fetched from the network.
     *
     * @example
     * ```
     * const nodeDataProvider = new MyNodeDataProvider();
     *
     * nodeDataProvider.getData(node, (data) => {
     * 	console.log('Node data:', data);
     * });
     * ```
     *
     * @param node The node (or its ProseMirror representation) for which to fetch data.
     * @param callback The callback function to call with the fetched data or an error.
     */
  }, {
    key: "getData",
    value: function getData(node, callback) {
      // Move implementation to a separate async method
      // to keep this method synchronous and avoid async/await in the public API.
      void this.getDataAsync(node, callback);
    }
  }, {
    key: "getDataAsync",
    value: function () {
      var _getDataAsync = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(node, callback) {
        var jsonNode, _dataKey, dataFromCache, cacheVersionBeforeRequest, dataPromise, data;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;
              jsonNode = 'toJSON' in node ? node.toJSON() : node;
              if (this.isNodeSupported(jsonNode)) {
                _context.next = 5;
                break;
              }
              // eslint-disable-next-line no-console
              console.error("The ".concat(this.constructor.name, " doesn't support Node ").concat(jsonNode.type, "."));
              return _context.abrupt("return");
            case 5:
              _dataKey = this.nodeDataKey(jsonNode);
              dataFromCache = this.cache[_dataKey];
              if (!(dataFromCache !== undefined)) {
                _context.next = 20;
                break;
              }
              if (!isPromise(dataFromCache.data)) {
                _context.next = 17;
                break;
              }
              _context.t0 = callback;
              _context.next = 12;
              return dataFromCache.data;
            case 12:
              _context.t1 = _context.sent;
              _context.t2 = {
                data: _context.t1
              };
              (0, _context.t0)(_context.t2);
              _context.next = 18;
              break;
            case 17:
              callback({
                data: dataFromCache.data
              });
            case 18:
              if (!isSSR()) {
                _context.next = 20;
                break;
              }
              return _context.abrupt("return");
            case 20:
              if (!((dataFromCache === null || dataFromCache === void 0 ? void 0 : dataFromCache.source) !== 'network')) {
                _context.next = 29;
                break;
              }
              // Store the current cache version before making the request,
              // so we can check if the cache has changed while we are waiting for the network response.
              cacheVersionBeforeRequest = this.cacheVersion;
              dataPromise = this.fetchNodesData([jsonNode]).then(function (_ref3) {
                var _ref4 = _slicedToArray(_ref3, 1),
                  value = _ref4[0];
                return value;
              }); // Store the promise in the cache to avoid multiple requests for the same data
              this.cache[_dataKey] = {
                source: 'network',
                data: dataPromise
              };
              _context.next = 26;
              return dataPromise;
            case 26:
              data = _context.sent;
              // We need to call the callback with the data with result even if the cache version has changed,
              // so all promises that are waiting for the data can resolve.
              callback({
                data: data
              });

              // If the cache version has changed, we don't want to use the data from the network
              // because it could be stale data.
              if (cacheVersionBeforeRequest === this.cacheVersion) {
                // Replace promise with the resolved data in the cache
                this.cache[_dataKey] = {
                  source: 'network',
                  data: data
                };
              }
            case 29:
              _context.next = 34;
              break;
            case 31:
              _context.prev = 31;
              _context.t3 = _context["catch"](0);
              // If an error occurs, we call the callback with the error
              callback({
                error: _context.t3 instanceof Error ? _context.t3 : new Error(String(_context.t3))
              });
            case 34:
            case "end":
              return _context.stop();
          }
        }, _callee, this, [[0, 31]]);
      }));
      function getDataAsync(_x, _x2) {
        return _getDataAsync.apply(this, arguments);
      }
      return getDataAsync;
    }()
    /**
     * Fetches data for a given node and returns it as a Promise.
     * This is a convenience wrapper around the `data` method for use with async/await.
     *
     * Note: This promise resolves with the *first* available data, which could be from the SSR cache or the network.
     * It may not provide the most up-to-date data if a network fetch is in progress.
     *
     * Note: This method is only for migration purposes. Use {@link getData} in new code instead.
     *
     * @private
     * @deprecated Don't use this method, use {@link getData} method instead.
     *             This method is only for migration purposes.
     *
     * @param node The node (or its ProseMirror representation) for which to fetch data.
     * @returns A promise that resolves with the node's data.
     */
  }, {
    key: "getDataAsPromise_DO_NOT_USE_OUTSIDE_MIGRATIONS",
    value: function getDataAsPromise_DO_NOT_USE_OUTSIDE_MIGRATIONS(node) {
      var _this = this;
      return new Promise(function (resolve, reject) {
        try {
          _this.getData(node, function (payload) {
            if (payload.error) {
              reject(payload.error);
            } else {
              resolve(payload.data);
            }
          });
        } catch (error) {
          reject(error);
        }
      });
    }

    /**
     * Checks the cache for the given node and returns its status.
     *
     * Possible return values:
     * - `false`: No cached data found for the node.
     * - `'ssr'`: Data is cached from server-side rendering (SSR).
     * - `'network'`: Data is cached from a network request.
     *
     * @param node The node (or its ProseMirror representation) to check in the cache.
     * @returns The cache status: `false`, `'ssr'`, or `'network'`.
     */
  }, {
    key: "getCacheStatusForNode",
    value: function getCacheStatusForNode(node) {
      var jsonNode = 'toJSON' in node ? node.toJSON() : node;
      if (!this.isNodeSupported(jsonNode)) {
        // eslint-disable-next-line no-console
        console.error("The ".concat(this.constructor.name, " doesn't support Node ").concat(jsonNode.type, "."));
        return false;
      }
      var dataKey = this.nodeDataKey(jsonNode);
      var dataFromCache = this.cache[dataKey];
      return dataFromCache ? dataFromCache.source : false;
    }
  }]);
}();
function isPromise(value) {
  return _typeof(value) === 'object' && value !== null && 'then' in value && typeof value.then === 'function';
}